// @ts-nocheck
import {
  Button,
  ButtonGroup,
  DialogActionTrigger,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { FaExchangeAlt } from "react-icons/fa";

import { type ApiError, type TablePublic, TablesService } from "@/client";
import useCustomToast from "@/hooks/useCustomToast";
import { handleError } from "@/utils";
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Field } from "../ui/field";

interface EditTableProps {
  table: TablePublic;
}

interface TableUpdateForm {
  table_name: string;
  description: string;
}

const EditTable = ({ table }: EditTableProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccessToast } = useCustomToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TableUpdateForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      table_name: table.table_name,
      description: table.description,
    },
  });

  const mutation = useMutation({
    mutationFn: (data: TableUpdateForm) =>
      TablesService.updateTable({ id: table.id, requestBody: data }),
    onSuccess: () => {
      showSuccessToast("테이블이 성공적으로 수정되었습니다.");
      reset();
      setIsOpen(false);
      setHasSubmitted(false);
    },
    onError: (err: ApiError) => {
      handleError(err);
      setHasSubmitted(false);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "tables" || query.queryKey[0] === "tablesList",
      });
    },
  });

  const onSubmit: SubmitHandler<TableUpdateForm> = async (data) => {
    if (hasSubmitted) return;
    setHasSubmitted(true);

    mutation.mutate(data);
  };

  return (
    <DialogRoot
      size={{ base: "xs", md: "md" }}
      placement="center"
      open={isOpen}
      onOpenChange={({ open }) => {
        setIsOpen(open);
        if (open) {
          // 다이얼로그가 열릴 때 기존 테이블 데이터를 폼에 채워 넣음
          reset({
            table_name: table.table_name,
            description: table.description,
          });
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost">
          <FaExchangeAlt fontSize="16px" />
          테이블 수정
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>테이블 수정</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Text mb={4}>아래 테이블 세부 정보를 업데이트하세요.</Text>
            <VStack gap={4}>
              <Field
                required
                invalid={!!errors.table_name}
                errorText={errors.table_name?.message}
                label="이름"
              >
                <Input
                  id="table_name"
                  {...register("table_name", {
                    required: "Table name is required",
                  })}
                  placeholder="테이블 이름"
                  type="text"
                />
              </Field>

              <Field
                required
                invalid={!!errors.description}
                errorText={errors.description?.message}
                label="설명"
              >
                <Input
                  id="description"
                  {...register("description")}
                  placeholder="테이블 설명"
                  type="text"
                />
              </Field>
            </VStack>
          </DialogBody>

          <DialogFooter gap={2}>
            <ButtonGroup>
              <DialogActionTrigger asChild>
                <Button
                  variant="subtle"
                  colorPalette="gray"
                  disabled={isSubmitting}
                >
                  취소
                </Button>
              </DialogActionTrigger>
              <Button
                variant="solid"
                type="submit"
                disabled={isSubmitting || hasSubmitted}
                loading={isSubmitting}
              >
                저장
              </Button>
            </ButtonGroup>
          </DialogFooter>
        </form>
        <DialogCloseTrigger />
      </DialogContent>
    </DialogRoot>
  );
};

export default EditTable;
